/**
 * Validate kết hợp cho mã số thuế và CCCD chủ hộ (CCCD)
 * - Nếu có cả hai, validate từng phần
 * - Nếu chỉ có một, validate phần đó
 * @param {string} value Giá trị cần kiểm tra, dạng "TaxCode/IdentificationNumber"
 * @returns {boolean} Kết quả kiểm tra (true: hợp lệ, false: không hợp lệ)
 */
const validateTaxCodeOrIdentification = value => {
	if (!value) return false;
	const val = value.trim();
	return validateIdentificationNumber(val) || validateTaxCode(val);
};

/**
 * Kiểm tra CCCD chủ hộ có đúng định dạng không
 * - Phải là dạng số
 * - Phải có đúng 12 chữ số
 * @param {string} value Giá trị cần kiểm tra
 * @returns {boolean} Kết quả kiểm tra (true: hợp lệ, false: không hợp lệ)
 */
const validateIdentificationNumber = (value, isEmptyNoValid = true) => {
	try {
		if (isEmptyNoValid && !value) {
			return true; // Giá trị trống được xử lý bởi rule 'required' nếu cần
		}

		if (value) {
			value = value.trim();
		}

		// Biểu thức chính quy kiểm tra chính xác 12 chữ số
		const oRegex = /^[0-9]{12}$/;
		return oRegex.test(value);
	} catch (e) {
		return false;
	}
};

//Validate mã số thuế
const validateTaxCode = (value, isEmptyNoValid = true, isLoginCQT = false) => {
	try {
		if (isEmptyNoValid && !value) {
			return true; // Giá trị trống được xử lý bởi rule 'required' nếu cần
		}

		if (value) {
			value = value.trim();
		}

		//Rule cũ
		let oRegex = /^[0-9]{10}$|^[0-9]{13}$|^[0-9]{10}(\s|-)[0-9]{3}$/;
		//Rule mới sửa login bằng MST có định dạng 0101243150-999-U001
		if (isLoginCQT) {
			oRegex = /^[0-9]{10}$|^[0-9]{13}$|^[0-9]{10}(\s|-)[0-9]{3}$|^[0-9]{10}(\s|-)[0-9]{3}-U[0-9]{3}$/;
		}

		if (oRegex.test(value)) {
			let arrcode = [],
				lengthCode = value.length;
			for (let i = 0; i < lengthCode; i++) {
				arrcode.push(value[i]);
			}

			let intTemp =
				31 * parseFloat(arrcode[0]) +
				29 * parseFloat(arrcode[1]) +
				23 * parseFloat(arrcode[2]) +
				19 * parseFloat(arrcode[3]) +
				17 * parseFloat(arrcode[4]) +
				13 * parseFloat(arrcode[5]) +
				7 * parseFloat(arrcode[6]) +
				5 * parseFloat(arrcode[7]) +
				3 * parseFloat(arrcode[8]);
			if (!(parseFloat(arrcode[9]) === 10 - (intTemp % 11))) {
				return false;
			}
		} else {
			return false;
		}
	} catch (e) {
		return false;
	}
	return true;
};

/**
 * Lấy mã số thuế hoặc số định danh kết hợp
 * @param {string} taxCode - Mã số thuế
 * @param {string} identificationNumber - Số định danh
 * @returns {string} - Chuỗi kết hợp các giá trị hợp lệ, ngăn cách bởi dấu "/"
 */
const getTaxCodeOrIdentification = (taxCode, identificationNumber) => {
	// Tạo mảng chứa các giá trị và trim whitespace
	let identityCodes = [taxCode ? taxCode.trim() : '', identificationNumber ? identificationNumber.trim() : ''];

	// Loại bỏ các giá trị null, undefined hoặc rỗng
	identityCodes = identityCodes.filter(x => x && x.length > 0);

	// Trả kết quả nối bằng dấu "/"
	return identityCodes.join('/');
};

/**
 * Validate username cho kết nối CQT
 * Kiểm tra username có khớp với identity codes của org không
 *
 * Quy tắc validate:
 * - MST: Hỗ trợ format identityCode hoặc identityCode-Uyyy
 * - CCCD: Chỉ so sánh exact match, không hỗ trợ suffix
 *
 * @param {string} username - Username cần validate
 * @param {Array<string>} identityCodes - Danh sách MST/CCCD của org [A, B]
 * @param {boolean} isBusinessHousehold - Có phải HKD không
 * @returns {boolean} true nếu hợp lệ
 *
 * @example
 * // HKD với MST=0101243150, CCCD=001234567890
 * validateUsernameForTaxaxion("0101243150", ["0101243150", "001234567890"], true) // true - exact match MST
 * validateUsernameForTaxaxion("0101243150-U001", ["0101243150", "001234567890"], true) // true - MST có suffix
 * validateUsernameForTaxaxion("001234567890", ["0101243150", "001234567890"], true) // true - exact match CCCD
 * validateUsernameForTaxaxion("001234567890-U001", ["0101243150", "001234567890"], true) // false - CCCD không được có suffix
 * validateUsernameForTaxaxion("9999999999", ["0101243150", "001234567890"], true) // false - không match
 *
 * // Công ty với MST=0101243150
 * validateUsernameForTaxaxion("0101243150", ["0101243150"], false) // true - exact match MST
 * validateUsernameForTaxaxion("0101243150-U001", ["0101243150"], false) // true - MST có suffix
 * validateUsernameForTaxaxion("001234567890", ["0101243150"], false) // false - không có CCCD
 */
const validateUsernameForTaxaxion = (username, identityCodes, isBusinessHousehold) => {
	try {
		// Validate đầu vào
		if (!username || !identityCodes || identityCodes.length === 0) {
			return false;
		}

		// Loại bỏ khoảng trắng thừa
		username = username.trim();
		const filteredCodes = identityCodes.filter(code => code && code.trim());

		// Kiểm tra xem username có suffix -Uyyy không
		const hasSuffix = /-U\d{3}$/.test(username);
		// Thông tin username có ký tự -Uyyy
		if (hasSuffix) {
			// Có suffix → chỉ cho phép với MST, không cho phép với CCCD
			const usernameBase = username.replace(/-U\d{3}$/, '');

			// Với TH có suffix này valid hiện tại chỉ hỗ trợ với MST
			if (!validateTaxCode(usernameBase, false)) {
				return false;
			}

			// Kiểm tra usernameBase có khớp với MST nào trong danh sách không
			return filteredCodes.some(code => {
				// Loại bỏ khoảng trắng thừa
				const trimmedCode = code.trim();

				// Chỉ so sánh với codes là MST
				return validateTaxCode(trimmedCode, false) && trimmedCode === usernameBase;
			});
		} else {
			// Không có suffix → có thể là MST hoặc CCCD (exact match)
			// Validate format
			let isValidFormat = false;
			if (isBusinessHousehold) {
				// HKD: có thể là MST hoặc CCCD
				isValidFormat = validateTaxCode(username, false) || validateIdentificationNumber(username, false);
			} else {
				// Công ty: chỉ được là MST
				isValidFormat = validateTaxCode(username, false);
			}

			// Nếu format không hợp lệ, trả về false
			if (!isValidFormat) {
				return false;
			}

			// So sánh exact match với danh sách identity codes
			return filteredCodes.some(code => code.trim() === username);
		}
	} catch (e) {
		return false;
	}
};

export {
	validateTaxCodeOrIdentification,
	validateTaxCode,
	validateIdentificationNumber,
	getTaxCodeOrIdentification,
	validateUsernameForTaxaxion
};
